import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { MadsWordmark } from "./MadsMark";
import { useCart } from "../context/CartContext";

const links = [
  { to: "/collections/everybody-looks", label: "Everybody Looks", testid: "nav-link-collection" },
  { to: "/special", label: "Special", testid: "nav-link-special" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
  { href: "https://www.instagram.com/madsmade47/", label: "Instagram", testid: "nav-link-instagram" },
];

export const Navbar = () => {
  const { count, setIsOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "bg-[rgba(246,240,224,0.88)] backdrop-blur-xl border-b border-[rgba(42,33,24,0.06)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="px-6 sm:px-12 lg:px-20 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-mads" className="text-2xl select-none">
            <MadsWordmark />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) =>
              l.href ? (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={l.testid}
                  className="font-mono-accent text-[11px] uppercase tracking-[0.28em] text-[#7A6C5A] hover:text-[#2A2118] transition-colors duration-300"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={l.testid}
                  className="font-mono-accent text-[11px] uppercase tracking-[0.28em] text-[#7A6C5A] hover:text-[#2A2118] transition-colors duration-300"
                >
                  {l.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-4">
            <button
              data-testid="nav-cart-trigger"
              onClick={() => setIsOpen(true)}
              className="relative flex items-center gap-2 border border-[rgba(42,33,24,0.18)] hover:border-[#C9501C] px-4 py-2 rounded-full transition-colors duration-300 group"
            >
              <ShoppingBag size={15} className="text-[#2A2118] group-hover:text-[#C9501C] transition-colors" />
              <span className="font-mono-accent text-[11px] tracking-widest text-[#2A2118]">
                {count}
              </span>
            </button>
            <button
              data-testid="nav-mobile-menu-button"
              className="md:hidden text-[#2A2118]"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-0 top-20 z-40 bg-[#F8EDDA] border-b border-[rgba(42,33,24,0.12)] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) =>
                l.href ? (
                  <a
                    key={l.label}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-${l.testid}`}
                    onClick={() => setMobileOpen(false)}
                    className="font-mono-accent text-xs uppercase tracking-[0.28em] text-[#2A2118]"
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link
                    key={l.label}
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    onClick={() => setMobileOpen(false)}
                    className="font-mono-accent text-xs uppercase tracking-[0.28em] text-[#2A2118]"
                  >
                    {l.label}
                  </Link>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
